export type WorkOrderStatus = "IN_PRODUCTION" | "WAITING_ENGINEERING" | "QUALITY_CONTROL";

export type WorkOrderPriority = "HIGH" | "MEDIUM" | "NORMAL";

export type WorkOrderStage =
  | "PAINT"
  | "METALWORK"
  | "QUALITY_CONTROL_STAGE";

export interface WorkOrderTableRow {
  id: number;
  workOrderId: string;
  status: WorkOrderStatus;
  customerName: string;
  vehicle: string;
  currentStage: WorkOrderStage;
  progress: number;
  priority: WorkOrderPriority;
}

export interface WorkOrderVehicleDetails {
  workOrderNo: string;
  customerName: string;
  subCustomer: string;
  chassis: string;
  manufacturer: string;
  model: string;
  axleStructure: string;
  year: string;
  boxType: string;
}

export type StatusFilter = "ALL" | WorkOrderStatus;

export enum DiagramDepartmentTab {
  DIAGRAM = 0,
  BENDING = 1,
  CUTTING = 2,
  FRONT_REAR_WALL = 3,
  ROOF = 4,
}

export type DiagramInfoField = {
  labelKey: string;
  value: string;
};

export type DiagramSectionLayout = "default" | "columnFlow" | "singleColumn";

export type DiagramSection = {
  titleKey: string;
  fields: DiagramInfoField[];
  layout: DiagramSectionLayout;
};

export type DiagramPageLayout =
  | "overview"
  | "sections"
  | "toolsAndNotes"
  | "singleSection";

export type DiagramCustomNotes = {
  titleKey: string;
  notes: string[];
};

export type DiagramPageData = {
  layout?: DiagramPageLayout;
  projectInfo?: DiagramInfoField[];
  customerVehicle?: DiagramInfoField[];
  technicalSpec?: DiagramInfoField[];
  sections?: DiagramSection[];
  section?: DiagramSection;
  toolboxesAndAccessories?: DiagramInfoField[];
  customNotes?: DiagramCustomNotes;
};

export type DiagramViewState = {
  activeDepartment: DiagramDepartmentTab;
  activePage: number;
  pages: DiagramPageData[];
};

export enum WorkOrderDetailTab {
  PRICE_QUOTE = 0,
  VEHICLE_DETAILS = 1,
  CENTER_OF_GRAVITY = 2,
  BOX_DETAILS = 3,
  ADDONS = 4,
  WEIGHT_DATA = 5,
  LOAD_DISTRIBUTION = 6,
  PICTURES = 7,
}

export type BoxDetailsFormValues = {
  recommended: string;
  minimum: string;
  frontSpacing: string;
  minLength: string;
  maxLength: string;
  generalLength: string;
  boxLength: string;
  centerOfGravity: string;
  centerOfGravityTo: string;
  centerOfGravityRange: string;
  maxRearOverhang: string;
  rearOverhangPlus: string;
  rearOverhang: string;
  floorSideHeight: string;
  floorType: string;
  floor: string;
  maxWidth: string;
  boxWidth: string;
  floorHeight: string;
  crossbeams: string;
  subChassis: string;
  rearWallType: string;
  rearWall: string;
  frontHeight: string;
  frontType: string;
  sideDoorsQuantity: string;
  sideDoors: string;
  pillarsQuantity: string;
  pillars: string;
  comment: string;
  clampingLocks: boolean;
  rearDoor: string;
  sideRail: string;
  roofBeam: string;
  roofMaterial: string;
  panelInternal: string;
  maximum: string;
  sideLightOpening: string;
  rearLightOpening: string;
  hinge: string;
  rearRail: string;
};

export type AddOnItemFormValue = {
  enabled: boolean;
  selectedOption: string;
  note: string;
  quantity: string;
  size: string;
  location: string;
  itemType: string;
  optionEnabled: boolean;
};

export type AddOnFormValues = {
  sideGuard: AddOnItemFormValue;
  mudguard: AddOnItemFormValue;
  sideLighting: AddOnItemFormValue;
  interiorLighting: AddOnItemFormValue;
  rearLights: AddOnItemFormValue;
  heightLighting: AddOnItemFormValue;
  reverseProjector: AddOnItemFormValue;
  pipeMounts: AddOnItemFormValue;
  microphoneLights: AddOnItemFormValue;
  camera: AddOnItemFormValue;
  frontCableTensioner: AddOnItemFormValue;
  sideCableTensioner: AddOnItemFormValue;
  strapTensioners: AddOnItemFormValue;
  apcRings: AddOnItemFormValue;
  recessedRings: AddOnItemFormValue;
  loadSpreaderDevice: AddOnItemFormValue;
  waterJerrycan: AddOnItemFormValue;
  polesBasket: AddOnItemFormValue;
  palletJackMount: AddOnItemFormValue;
  spareMount: AddOnItemFormValue;
  rearLightProtection: AddOnItemFormValue;
  rearBumper: AddOnItemFormValue;
  frontCraneWings: AddOnItemFormValue;
  hazardousMaterials: AddOnItemFormValue;
  longTransportTriangle: AddOnItemFormValue;
  reflectors: AddOnItemFormValue;
  dieselLock: AddOnItemFormValue;
  batteriesLock: AddOnItemFormValue;
  ureaLock: AddOnItemFormValue;
  spareWheelLock: AddOnItemFormValue;
  hydraulicTailLiftLock: AddOnItemFormValue;
  hydraulicTailLiftMotorLock: AddOnItemFormValue;
  towHook: AddOnItemFormValue;
};

export type AddOnTableRow = {
  id: string;
  location: string;
  itemType: string;
  quantity: string;
  note: string;
  height: string;
};

export type AddOnTableKey = "toolboxes" | "openBasket" | "tyingStrip";

export type AddOnTablesState = Record<AddOnTableKey, AddOnTableRow[]>;

export type AddOnTyingStripModalState = {
  open: boolean;
  height: string;
  note: string;
};

export type AddOnDetailsState = {
  form: AddOnFormValues;
  tables: AddOnTablesState;
  tyingStripModal: AddOnTyingStripModalState;
};

export type WeightAndWallFormValues = {
  chassisWeight: string;
  netBox: string;
  frontWeighing: string;
  rearWeighing: string;
  generalWeighing: string;
  wallWeight: string;
  wallManufacturer: string;
  wallNumber: string;
};

export type LoadDistributionFormValues = {
  axleDistanceCalculation: string;
  centerOfGravityCalculation: string;
  offsetCalculation: string;
  rearAxle: string;
  generalSelf: string;
  frontAxle: string;
  fuelLevel: string;
  afterWeighingData: string;
  weighingComments: string;
  driverAndPassengers: string;
  wallType: string;
  hydraulicWallNumber: string;
};

export type LoadDistributionPdfTableRow = {
  labelKey: string;
  calculation: string;
  measurement: string;
};

export type LoadDistributionPdfFuelInfoField = {
  labelKey: string;
  valueKey: string;
  ltr?: boolean;
};

export type LoadDistributionPdfWeighingNote = {
  labelKey: string;
  valueKey?: string;
  value?: string;
};

export type LoadDistributionPdfHydraulicWallRow = {
  wallNumber: string;
  chassisNo: string;
};

export type LoadDistributionPdfWeightDistributionRow = {
  labelKey: string;
  front: string;
  rear: string;
  general: string;
};

export type LoadDistributionPdfData = {
  projectInfo: DiagramInfoField[];
  customerVehicle: DiagramInfoField[];
  technicalSpec: DiagramInfoField[];
  calculationTableRows: LoadDistributionPdfTableRow[];
  weighingNotes: LoadDistributionPdfWeighingNote[];
  hydraulicWall: {
    titleKey: string;
    columns: {
      wallNumberKey: string;
      chassisNoKey: string;
    };
    rows: LoadDistributionPdfHydraulicWallRow[];
  };
  weightDistribution: {
    titleKey: string;
    titleSuffixKey: string;
    columns: {
      detailKey: string;
      frontKey: string;
      rearKey: string;
      generalKey: string;
    };
    rows: LoadDistributionPdfWeightDistributionRow[];
  };
  manualWeighingForm: {
    rows: { valueKey: string }[];
  };
  fuelInfo: {
    fields: LoadDistributionPdfFuelInfoField[];
  };
};

export type VehicleDetailsFormValues = {
  manufacturer: string;
  year: string;
  model: string;
  licenseNo: string;
  chassisNo: string;
  driverCab: string;
  passengers: string;
  drive: string;
  tire: string;
  rearSuspension: string;
  wheelbase: string;
  frontAxle: string;
  rearAxle: string;
  frontOverhang: string;
  chassisWidth: string;
  chassisHeight: string;
  totalHeight: string;
  chassisWeight: string;
  netBodyWeight: string;
  licenseWeight: string;
  frontLoad: string;
  rearLoad: string;
  hydraulic: string;
  transferBody: boolean;
};

export type QuotationFormValues = {
  totalSale: string;
  profitAmount: string;
  profitPercent: string;
  theoreticalPrice: string;
  totalCost: string;
  totalMaterialCost: string;
  workHours: string;
  perHour: string;
  totalWorkCost: string;
  repairHours: string;
  sheetMetalCost: string;
  craneCompletionAddition: string;
  exchangeRate: string;
  otherSaleItem: string;
  variableCoefficient: string;
  downPayment: string;
  vehicleWeightWithoutSide: string;
  assembledLength: string;
  assembledWidth: string;
  floorBase: string;
  floorType: string;
  floorThickness: string;
  crossBeams: string;
  reinforcedModel: boolean;
};

export type QuotationFrontWallFields = {
  enabled: boolean;
  selectedOption: string;
  height: string;
  treadSurface: boolean;
  treadNote: string;
  widthReinforcement: boolean;
  widthNote: string;
};

export type QuotationBoxStructureState = {
  frontWall: QuotationFrontWallFields;
  backWall: AddOnItemFormValue;
  frontRack: AddOnItemFormValue;
  backRack: AddOnItemFormValue;
  sideDoors: AddOnItemFormValue;
  pillars: AddOnItemFormValue;
  hydraulicSide: AddOnItemFormValue;
  rearDoor: AddOnItemFormValue;
};

export type QuotationEquipmentTableKey = "toolboxes" | "openBasket";

export type QuotationTyingEquipmentState = {
  sideCableTensioner: AddOnItemFormValue;
  strapTensioners: AddOnItemFormValue;
  frontCableTensioner: AddOnItemFormValue;
  recessedRings: AddOnItemFormValue;
  apcRings: AddOnItemFormValue;
  additionalTyingHooks: AddOnItemFormValue;
  toolboxes: AddOnTableRow[];
  openBasket: AddOnTableRow[];
  polesBasket: AddOnItemFormValue;
  waterJerrycan: AddOnItemFormValue;
  palletJackMount: AddOnItemFormValue;
};

export type QuotationTyingEquipmentCardKey = Exclude<
  keyof QuotationTyingEquipmentState,
  QuotationEquipmentTableKey
>;

export type QuotationSafetyVehicleState = {
  spareWheelCarrier: AddOnItemFormValue;
  hazardousMaterialsMounts: AddOnItemFormValue;
  rearBumper: AddOnItemFormValue;
  couplingLocks: AddOnItemFormValue;
  extinguisherMounts: AddOnItemFormValue;
  rearLightMounts: AddOnItemFormValue;
  wings: AddOnItemFormValue;
  roundIronSides: AddOnItemFormValue;
};

export type QuotationLocksState = {
  dieselLock: AddOnItemFormValue;
  ureaLock: AddOnItemFormValue;
  batteriesLock: AddOnItemFormValue;
  hydraulicTailLiftMotorLock: AddOnItemFormValue;
  hydraulicTailLiftLock: AddOnItemFormValue;
  spareWheelLock: AddOnItemFormValue;
};

export type QuotationStandard95State = {
  sideGuardsReflectors: AddOnItemFormValue;
  sideLighting: AddOnItemFormValue;
  standardMudguards: AddOnItemFormValue;
};

export type CentreOfGravityStatus = "OK" | "NOT_OK";

export type CentreOfGravityAdjustmentMode = "SET_MAX_LENGTH" | "SET_MIN_VALUE";

export type CentreOfGravityFormValues = {
  calculatedValue: string;
  minRange: string;
  maxRange: string;
  centerOfGravityStatus: CentreOfGravityStatus;
  currentLength: string;
  minimalLength: string;
  maximalLength: string;
  vehicleLengthStatus: CentreOfGravityStatus;
  adjustmentMode: CentreOfGravityAdjustmentMode;
};

export type ChasisLoad = "pwo" | "pwf" | "pwr" | "swo" | "swf" | "swr";

export type ChassisLoadRow = {
  value: string;
  calculationValue: string;
};

export type ChassisLoadsState = Record<ChasisLoad, ChassisLoadRow>;
export type VehicleMeasureCode = "wb" | "cg" | "fo" | "g" | "k" | "rg" | "lbox" | "hist" | "dg";
export type VehicleMeasuresState = Record<VehicleMeasureCode, ChassisLoadRow>;
export type AdditionalEquipmentCode = "np1" | "np2" | "wr" | "rcg" | "wc" | "ccg";
export type AdditionalEquipmentState = Record<AdditionalEquipmentCode, ChassisLoadRow>;

export interface WorkOrderState {
  loading: boolean;
  detailLoading: boolean;
  error: string | null;
  statusFilter: StatusFilter;
  workOrders: WorkOrderTableRow[];
  selectedWorkOrderId: number | null;
  vehicleDetails: WorkOrderVehicleDetails | null;
  vehicleDetailsForm: VehicleDetailsFormValues | null;
  boxDetailsForm: BoxDetailsFormValues | null;
  weightAndWallForm: WeightAndWallFormValues | null;
  loadDistributionForm: LoadDistributionFormValues | null;
  addOnDetails: AddOnDetailsState | null;
  quotationForm: QuotationFormValues | null;
  quotationBoxStructure: QuotationBoxStructureState | null;
  quotationTyingEquipment: QuotationTyingEquipmentState | null;
  quotationSafetyVehicle: QuotationSafetyVehicleState | null;
  quotationLocks: QuotationLocksState | null;
  quotationStandard95: QuotationStandard95State | null;
  centreOfGravityForm: CentreOfGravityFormValues | null;
  chassisLoads: ChassisLoadsState | null;
  vehicleMeasures: VehicleMeasuresState | null;
  additionalEquipment: AdditionalEquipmentState | null;
  diagramView: DiagramViewState;
  activeDetailTab: WorkOrderDetailTab;
}
